// File: src/components/EventPreview/EventDetails.jsx
import React, { useState } from 'react';
import axios from 'axios';
import { ReactComponent as Tribeverified } from '../../assets/TribeVerified.svg';
import { LuHeartHandshake, LuMapPin, LuPartyPopper, LuBaby, LuShieldCheck, LuClock, LuStar, LuTicket, LuMail, LuLock } from 'react-icons/lu';
import TicketCard from './TicketCard';
import TicketPurchasedSheet from '../TicketPurchase/TicketPurchasedSheet';
import QrViewerSheet from '../TicketPurchase/QrViewerSheet';
import TicketValueBanner from './TicketValueBanner';
import WhereToMeetCard from "./WhereToMeetCard";
import WhereToMeetSheet from "../WhereToMeetSheet";
import { useNavigate } from 'react-router-dom';
import HostAgendaCard from './HostAgendaCard';
import EventDescription from './EventDescription';

function tenureLabelFrom(dateLike) {
  if (!dateLike) return '';
  const start = new Date(dateLike);
  if (Number.isNaN(start.getTime())) return '';
  const now = new Date();
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1; // partial month adjust
  if (months <= 0) return 'New host';
  if (months < 12) return `${months} ${months === 1 ? 'month' : 'months'} hosting`;
  const years = Math.floor(months / 12);
  return `${years} ${years === 1 ? 'year' : 'years'} hosting`;
}


function isNeutralEvent(event) {
  return String(event?.mode || '').toLowerCase() === 'neutral';
}

function genderGateLabelAny(event) {
  const g = String(
    event?.neutralGenderGate ??
    event?.classicGenderGate ??
    event?.genderGate ??
    'all'
  ).trim().toLowerCase();

  if (g === 'men_only' || g === 'menonly') return 'Men only';
  if (g === 'women_only' || g === 'womenonly') return 'Women only';
  return '';
}

// ✅ Neutral-only “sexual orientation” label (NO gating)
function neutralOrientationLabel(event) {
  // 1) If they tagged LGBTQ, show LGBTQ+ (strongest signal)
  const tags = Array.isArray(event?.tags) ? event.tags.map(t => String(t).toLowerCase()) : [];
  if (tags.includes('lgbtq')) return 'LGBTQ+';

  // 2) Otherwise show the neutralOrientation dropdown value
  const o = String(event?.neutralOrientation || 'open').trim().toLowerCase();
  if (o === 'lgbtq' || o === 'lgbtq+') return 'LGBTQ+';
  if (o === 'gay_men') return 'Gay Men';
  if (o === 'gay_women') return 'Gay Women';
  if (o === 'straight' || o === 'heterosexual') return 'Heterosexual';

  // open/empty => show nothing
  return '';
}

/* ───────────────────────── helpers */
const headFrom = (e) =>
  e?.attendeeCount ??
  (Array.isArray(e?.attendees) ? e.attendees.length : undefined) ??
  (Array.isArray(e?.attendeeIds) ? e.attendeeIds.length : 0);

function toHeaderDate(str = '') {
  if (!str) return '';
  const [y, m, d] = str.split('-').map(Number);
  if (!y) return '';
  const dt = new Date(y, (m || 1) - 1, d || 1);
  const WD = ['Sun.', 'Mon.', 'Tue.', 'Wed.', 'Thu.', 'Fri.', 'Sat.'];
  const MO = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.'];
  return `${WD[dt.getDay()]} ${MO[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
}

function toLongDate(str = '') {
  if (!str) return '';
  const [y, m, d] = str.split('-').map(Number);
  if (!y) return '';
  return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

const timeRange = (s = '', e = '') => (s || e ? `${s || ''}${s && e ? ' – ' : ''}${e || ''}` : '');

const formatPrice = (val) => {
  if (val === 0 || val === '0') return '$0';
  if (typeof val === 'number' && Number.isFinite(val)) return `$${val}`;
  if (typeof val === 'string') {
    const t = val.trim();
    if (/^\$?\d+(\.\d{1,2})?$/.test(t)) return t.startsWith('$') ? t : `$${t}`;
    if (/free/i.test(t)) return '$0';
  }
  return '$0';
};

// Normalize purpose tag to label + copy
function purposeMeta(raw = '') {
  const s = String(raw).trim().toLowerCase();

  if (s.startsWith('friend') || s.startsWith('platonic') || s.startsWith('social')) {
    return { label: 'Friendship', copy: 'For attendees looking to meet new friends and build community.' };
  }
  if (s.startsWith('network') || s.startsWith('career') || s.startsWith('prof')) {
    return { label: 'Networking', copy: 'For professionals aiming to make career connections and exchange ideas.' };
  }
  if (s.startsWith('dat') || s.startsWith('romance')) {
    return { label: 'Dating', copy: 'For people seeking new romantic connections.' };
  }
  return { label: raw || 'Purpose', copy: '' };
}

/* Orientation label for the details header line (used only when purpose = Dating) */
function orientationLabelForDetails(event) {
  // Only show for Dating
  const purpose = String(event?.purposeTag || '').toLowerCase();
  if (purpose !== 'dating') return '';

  // Prefer Classic → Mixer → Reveal
  const raw = String(
    event?.classicOrientation ||
    event?.orientationType ||
    event?.revealMode?.orientation ||
    'open'
  ).toLowerCase().replace(/[^a-z]/g, '');

  if (raw === 'gaymen' || raw === 'mlm' || raw === 'menlovingmen' || raw === 'gay_men') return 'Gay Men';
  if (raw === 'gaywomen' || raw === 'wlw' || raw === 'womenlovingwomen' || raw === 'lesbian' || raw === 'gay_women') return 'Gay Women';
  if (raw === 'straight' || raw === 'hetero' || raw === 'heterosexual' || raw === 'menandwomen') return 'Heterosexual';

  // Merge "bisexual" into open, and treat all "open/all" variants as Gender Fluid
  if (raw === 'open' || raw === 'bisexual' || raw === 'bi' || raw === 'all' || raw === 'allgenders' || raw === 'allgender' || raw === 'any' || raw === 'anygender' || raw === 'anygenders') {
    return 'Gender Fluid';
  }

  return ''; // unknown -> omit
}


/* Gender gate chip for classic Friendship/Networking events */
/* Gender gate chip for classic Friendship/Networking events */
function genderGateChipForDetails(event) {
  // ✅ Neutral uses neutralGenderGate
  // ✅ Classic uses classicGenderGate
  // ✅ Mixer/legacy fallback uses genderGate
  const g = String(
    event?.neutralGenderGate ??
    event?.classicGenderGate ??
    event?.genderGate ??
    'all'
  )
    .trim()
    .toLowerCase();

  if (g === 'men_only' || g === 'menonly') return 'Men only';
  if (g === 'women_only' || g === 'womenonly') return 'Women only';
  return ''; // 'all' → no chip
}


function ageLabelForDetails(event) {
  if (!event?.ageGateOn) return '';
  const min = Math.max(18, Number(event.ageMin ?? 18));
  const max = event.ageMax == null ? null : Number(event.ageMax);
  if (max == null) return `${min}+`;
  if (min === max) return `${min} only`;
  return `${min}–${max}`;
}

function splitBracketedTitle(value = '') {
  const text = String(value || '').trim();
  const match = text.match(/^(.*?)\s*(\[[^\]]+\])\s*$/);

  if (!match) {
    return {
      mainTitle: text,
      bracketTitle: '',
    };
  }

  return {
    mainTitle: match[1].trim(),
    bracketTitle: match[2].trim(),
  };
}

function toFirstLastInitial(name = '') {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);

  if (!parts.length) return 'Host';

  const first = parts[0];
  const last = parts.length > 1 ? parts[parts.length - 1] : '';

  return last ? `${first} ${last.charAt(0)}.` : first;
}

/* ───────────────────────── palette */
const C = { ink: '#0B0B0B', sub: '#6B7280', hair: '#EEF0F2', chip: '#F4F4F5', white: '#FFFFFF' };

/* ───────────────────────── component */
export default function EventDetails({
  event = {},
  onVenueClick,
  onParticipantsClick,
  onPriceClick,
  onVerifiedClick,
  isAuthed = false,
  isHostViewer = false,
  isRecommended = false,
}) {
  const navigate = useNavigate();

  const {
    _id,
    name = 'Untitled Event',
    hostedAt = '',
    address = '',
    eventDate = '',
    startTime12 = '',
    endTime12 = '',
    price = 0,
    attendees = [],
    attendeeIds = [],
    capacity: rawCap = 0,
    promoterId = {},
    purposeTag = '',
  } = event;

  const currentUserId = (() => {
    try {
      const auth = JSON.parse(
        localStorage.getItem('tribe_auth') || '{}'
      );

      return String(
        auth?.uid ||
          auth?.userId ||
          auth?.user?._id ||
          localStorage.getItem('userId') ||
          ''
      );
    } catch {
      return String(
        localStorage.getItem('userId') || ''
      );
    }
  })();

  const capNum = Number(rawCap);
const capacity =
  Number.isFinite(Number(event?.displayCapacity))
    ? Number(event.displayCapacity)
    : (Number.isFinite(capNum) ? capNum : 0);

const attendeeTotal =
  Number.isFinite(Number(event?.displayAttendeeCount))
    ? Number(event.displayAttendeeCount)
    : (headFrom(event) || 0);
    const srcArr = Array.isArray(attendees) && attendees.length ? attendees : attendeeIds;
  const top3 = [0, 1, 2].map((i) => srcArr?.[i]).filter(Boolean);
  const extraCount = Math.max((attendeeTotal || 0) - top3.length, 0);

  const [showQr, setShowQr] = useState(false);
  const [qrForViewer, setQrForViewer] = useState(null);
  const [whereToMeetOpen, setWhereToMeetOpen] = useState(false);


  // host
 // host
const hostName =
  event?.hostName || promoterId?.displayName || promoterId?.name || 'Nati K.';



  const {
  mainTitle,
  bracketTitle,
} = splitBracketedTitle(name);

const displayHostName = toFirstLastInitial(hostName);

const hostCreatedAt =
  (promoterId && typeof promoterId === 'object' && (promoterId.hostSince || promoterId.createdAt)) || null;

const hostAvatar =
  promoterId?.avatarUrl ||
  promoterId?.profilePicUrl ||
  promoterId?.imageUrl ||
  `https://i.pravatar.cc/80?u=${encodeURIComponent(hostName)}`;
  const promoterEmail =
    (promoterId && typeof promoterId === 'object' && promoterId.email) ||
    event?.promoterEmail ||
    event?.hostEmail ||
    '';

  // Build a robust mailto: link
  const mailtoHref = React.useMemo(() => {
    const email = (promoterEmail || '').trim();
    const subject = `${name} — Event inquiry`;
    const body = `Hi ${hostName},

I’m reaching out about “${name}” on ${toLongDate(eventDate)}.`;
    if (!email) return 'mailto:';
    return `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  }, [promoterEmail, name, hostName, eventDate]);

  // base tickets-left (no lanes)
  const ticketsLeft = capacity > 0 ? Math.max(capacity - attendeeTotal, 0) : null;

  // ===== Lane awareness (Mixer hetero lanes OR Reveal-straight 50/50) =====
  const isRevealStraight =
    String(event?.mode || '').toLowerCase() === 'reveal' &&
    String(event?.revealMode?.orientation || '').toLowerCase() === 'straight' &&
    capacity > 0;

  // Prefer server-provided revealLane for reveal-straight
  const lane = isRevealStraight && event?.revealLane ? event.revealLane : null;

 // Lane caps: Mixer/Classic fields OR Neutral fields OR Reveal lane
const mixerHasLaneCaps =
  Number(event?.maleCap || 0) > 0 || Number(event?.femaleCap || 0) > 0;

const isNeutral = String(event?.mode || '').toLowerCase() === 'neutral';

const neutralMaleCap = Number(event?.neutralMaleCap || 0);
const neutralFemaleCap = Number(event?.neutralFemaleCap || 0);
const neutralHasLaneCaps = isNeutral && (neutralMaleCap > 0 || neutralFemaleCap > 0);

// We consider lanes if mixer lanes exist OR neutral lanes exist OR reveal-straight is active
const hasLaneCaps = mixerHasLaneCaps || neutralHasLaneCaps || !!lane;

// IMPORTANT: backend increments maleCount/femaleCount for lane systems (including neutral)
const maleTaken = Number.isFinite(Number(event?.maleCount)) ? Number(event.maleCount) : 0;
const femaleTaken = Number.isFinite(Number(event?.femaleCount)) ? Number(event.femaleCount) : 0;

const menLeft = lane
  ? Number(lane.maleRemaining ?? 0)
  : neutralHasLaneCaps && neutralMaleCap > 0
  ? Math.max(0, neutralMaleCap - maleTaken)
  : !isNeutral && Number(event?.maleCap || 0) > 0
  ? (
      Number.isFinite(event?.maleCount)
        ? Math.max(0, Number(event.maleCap) - Number(event.maleCount))
        : Number(event.maleCap)
    )
  : null;

const womenLeft = lane
  ? Number(lane.femaleRemaining ?? 0)
  : neutralHasLaneCaps && neutralFemaleCap > 0
  ? Math.max(0, neutralFemaleCap - femaleTaken)
  : !isNeutral && Number(event?.femaleCap || 0) > 0
  ? (
      Number.isFinite(event?.femaleCount)
        ? Math.max(0, Number(event.femaleCap) - Number(event.femaleCount))
        : Number(event.femaleCap)
    )
  : null;


  // (Adjust if you have an AuthContext; this is a safe fallback)
  function detectUserGender() {
    const g1 = (event?.currentUser?.gender || '').toString().toLowerCase();
    if (g1 === 'male' || g1 === 'female') return g1;
    try {
      const raw = localStorage.getItem('profile');
      if (raw) {
        const g2 = (JSON.parse(raw)?.gender || '').toString().toLowerCase();
        if (g2 === 'male' || g2 === 'female') return g2;
      }
    } catch {}
    return null; // guest/unknown
  }
  const userGender = detectUserGender();

const showLanes =
  (capacity > 0 && (mixerHasLaneCaps || isRevealStraight)) ||
  neutralHasLaneCaps;

  let displayLeft = ticketsLeft;

if (showLanes) {
  if (userGender === 'male' && Number.isFinite(menLeft)) {
    displayLeft = menLeft;
  } else if (userGender === 'female' && Number.isFinite(womenLeft)) {
    displayLeft = womenLeft;
  } else {
    const candidates = [menLeft, womenLeft].filter((n) => Number.isFinite(n));
    if (candidates.length) displayLeft = Math.max(...candidates);
  }
}

displayLeft = Number.isFinite(displayLeft) ? Math.max(0, displayLeft) : 0;

const participantsDisplay =
  capacity > 0 ? `${attendeeTotal || 0}/${capacity}` : `${attendeeTotal || 0}+`;

// consider a few common flags for "live"
const isLive = !!(event?.isLive || event?.live || /live/i.test(event?.status || ''));

  // purpose (tag + explanatory subheading)
  const { label: purposeLabel, copy: purposeCopy } = purposeMeta(event?.purpose || purposeTag);

  // Orientation (for purpose = Dating)
  const orientationLabel =
    purposeLabel === 'Dating' ? orientationLabelForDetails(event) : '';

  // ⬇️ state for ticket + sheet
  const [userTicket, setUserTicket] = React.useState(event?.userTicket || null);
  const [openTicket, setOpenTicket] = React.useState(null);


const isReservedForEvent =
  Boolean(userTicket) ||
  event?.attendeeIds?.some((id) => String(id) === String(currentUserId)) ||
  event?.attendees?.some((a) => String(a?._id || a) === String(currentUserId));


  // Keep local ticket in sync if parent passes a different one later
  React.useEffect(() => {
    if (event?.userTicket) setUserTicket(event.userTicket);
  }, [event?.userTicket]);

  // Helper to (re)fetch the user's ticket with cache-buster
  const refreshTicket = React.useCallback(async () => {
    const id = String(_id || '');
    if (!id) return;
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/tickets/${id}/mine?ts=${Date.now()}`,
        {
          headers: { Authorization: `Bearer ${token}`, 'X-Optional-Auth': '1' },
          withCredentials: true,
          meta: { allow401: true },
        }
      );
      const t = res.data?.userTicket || (res.data?.ok ? null : res.data) || null;
      setUserTicket(t); // null if none
    } catch {
      setUserTicket(null);
    }
  }, [_id]);

  const handleEmailHost = React.useCallback((e) => {
    try { e?.preventDefault?.(); } catch (_) {}

    const email = (promoterEmail || '').trim();

    console.log('[EventDetails] Email button clicked', {
      email,
      name,
      eventDate,
      hostName,
    });

    if (!email) {
      alert('No email is available for this host.');
      return;
    }

    const subject = `${name} — Event inquiry`;
    const body = `Hi ${hostName},

I’m reaching out about “${name}” on ${toLongDate(eventDate)}.`;

    const href = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    try {
      const a = document.createElement('a');
      a.href = href;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => document.body.removeChild(a), 0);
    } catch {
      window.location.href = href;
    }
  }, [promoterEmail, name, hostName, eventDate]);

  React.useEffect(() => {
    if (!isAuthed) {
      setUserTicket(null);
      return;
    }
    refreshTicket();
  }, [refreshTicket, isAuthed]);

  React.useEffect(() => {
    const onInvalidate = (e) => {
      if (String(e.detail?.eventId) !== String(_id)) return;
      setUserTicket(null);
      setOpenTicket(null);
      refreshTicket();
    };
    const onRefresh = (e) => {
      if (String(e.detail?.eventId) !== String(_id)) return;
      refreshTicket();
    };
    window.addEventListener('ticket:invalidate', onInvalidate);
    window.addEventListener('ticket:refresh', onRefresh);
    return () => {
      window.removeEventListener('ticket:invalidate', onInvalidate);
      window.removeEventListener('ticket:refresh', onRefresh);
    };
  }, [_id, refreshTicket]);

  const fallbackPurchasedAt = event?.userTicketPurchasedAt || event?.purchasedAt || Date.now();

  return (
    <section style={S.wrap}>
      <style>{`
        @keyframes livePulse {
          0% { transform: scale(1); opacity: .45; }
          70% { transform: scale(2.1); opacity: 0; }
          100% { transform: scale(1); opacity: 0; }
        }
      `}</style>

    {isLive && (
  <div style={S.liveWrap}>
    <div style={S.livePill}>
      <span style={S.liveDotWrap}>
        <span style={S.liveDotPulse} />
        <span style={S.liveDotCore} />
      </span>

      <span style={S.liveText}>Live</span>
    </div>
  </div>
)}

{isRecommended && (
  <div style={S.recommendedWrap}>
    <div style={S.recommendedBadge}>
      <LuStar
        size={13}
        strokeWidth={2.2}
      />

      <span>Picked for you</span>
    </div>
  </div>
)}

<h1 style={S.title}>

</h1>
<h1 style={S.title}>
  <span style={S.titleMain}>
    {mainTitle}
  </span>

  {bracketTitle && (
    <>
      {' '}
      <span style={S.titleBracket}>
        {bracketTitle}
      </span>
    </>
  )}
</h1>

      {hostedAt ? (
        <div style={S.hostRowTop}>
          <span style={S.hostedAtLabel}>Hosted at</span>
          {onVenueClick ? (
            <button type="button" onClick={onVenueClick} style={{ ...S.venueChip, ...S.buttonReset }}>
              {hostedAt}
            </button>
          ) : (
            <span style={S.venueChip}>{hostedAt}</span>
          )}
        </div>
      ) : null}

      {/* 3-column stats */}
      <div style={S.statsCard}>
        <div style={{ ...S.cardDivider, left: '33.333%' }} aria-hidden />
        <div style={{ ...S.cardDivider, left: '66.666%' }} aria-hidden />
        <div style={S.statsRow}>
       <div style={S.col}>
  {isReservedForEvent ? (
    <div style={S.reservedBubble}>
      <span style={S.reservedEmoji}>🎟️</span>
      <span>Reserved</span>
    </div>
  ) : (
    <>
      <div style={S.priceValue}>1 credit</div>
      <div style={S.caption}>TO RESERVE</div>
    </>
  )}
</div>
          <div style={S.col}>
            {onVerifiedClick ? (
              <button type="button" onClick={onVerifiedClick} style={{ ...S.buttonReset, ...S.verifiedAssetWrap }}>
                <Tribeverified style={S.verifiedSvg} />
              </button>
            ) : (
              <div style={S.verifiedAssetWrap}>
                <Tribeverified style={S.verifiedSvg} />
              </div>
            )}
          </div>

     <div style={S.col}>
  <div style={S.spotsValue}>
  {String(displayLeft).padStart(2, '0')}
</div>

  <div style={S.caption}>
    SPOTS LEFT
  </div>
</div>
        </div>
      </div>
      <EventDescription
        description={event.description}
        event={event}
      />

      {/* host strip */}
      <div style={S.hostSection}>
        <div style={S.hostCard}>
          <div style={S.hostStrip}>
            <div
              style={{
                ...S.hostAvatar,
                backgroundImage: `url(${hostAvatar})`,
              }}
            />

            <div style={S.hostTextAndBadge}>
              <div>
                <div style={S.hostTitle}>
                  Hosted by {displayHostName.toUpperCase()}
                </div>

                <div style={S.hostSub}>
                  {tenureLabelFrom(hostCreatedAt)}
                </div>
              </div>

              <span style={S.idBadge}>
                <LuShieldCheck
                  style={S.idBadgeIcon}
                  aria-hidden
                />
                ID Verified
              </span>
            </div>
          </div>
        </div>
      </div>

      {(isReservedForEvent || isHostViewer) && (
        <WhereToMeetCard
          onClick={() => setWhereToMeetOpen(true)}
        />
      )}

      {isHostViewer && event?.agendaPdfUrl && (
        <HostAgendaCard
          onClick={() => {
            const targetEventId = String(
              event?._id || _id || ''
            ).replace(/^neutral-/, '');

            navigate(
              `/neutral/${targetEventId}/agenda`,
              {
                state: { event },
              }
            );
          }}
        />
      )}

      {(isReservedForEvent || isHostViewer) && (
        <div style={S.afterMeetHairline} />
      )}

      {/* details list */}
      <div style={S.infoRow}>
        <div style={S.iconWrap}><LuMapPin style={S.icon} /></div>
        <div>
          <div style={S.rowTitle}>
            {hostedAt || 'Venue'}
          </div>
          {address ? <div style={S.rowSub}>{address}</div> : null}
        </div>
      </div>

      <div style={S.infoRow}>
        <div style={S.iconWrap}><LuClock style={S.icon} /></div>
        <div>
          <div style={S.rowTitle}>{toLongDate(eventDate)}</div>
          <div style={S.rowSub}>{timeRange(startTime12, endTime12)}</div>
        </div>
      </div>

   {/* purpose (with orientation OR classic gender gate chip) */}
<div style={S.infoRowLast}>
  <div style={S.iconWrap}><LuPartyPopper style={S.icon} /></div>
  <div>
    {(() => {
      const gateChip = genderGateChipForDetails(event);
      return (
        <div style={S.rowTitle}>
          {purposeLabel}
          {purposeLabel === 'Dating' && orientationLabel ? (
            <>
              <span style={S.dot}> • </span>
              <span>{orientationLabel}</span>
            </>
          ) : gateChip ? (
            <>
              <span style={S.dot}> • </span>
              <span>{gateChip}</span>
            </>
          ) : null}
        </div>
      );
    })()}
    {purposeCopy ? <div style={S.rowSub}>{purposeCopy}</div> : null}
  </div>
</div>
{/* Gender gate row (works for neutral too) */}
{(() => {
  const gate = genderGateLabelAny(event);
  if (!gate) return null;
  return (
    <div style={S.infoRowLast}>
      <div style={S.iconWrap}><LuHeartHandshake style={S.icon} /></div>
      <div>
        <div style={S.rowTitle}>Gender Gate</div>
        <div style={S.rowSub}>{gate}</div>
      </div>
    </div>
  );
})()}

{/* Sexual orientation row (NEUTRAL ONLY, label-only) */}
{isNeutralEvent(event) && (() => {
  const label = neutralOrientationLabel(event);
  if (!label) return null;
  return (
    <div style={S.infoRowLast}>
      <div style={S.iconWrap}><LuLock style={S.icon} /></div>
      <div>
        <div style={S.rowTitle}>Sexual Orientation</div>
        <div style={S.rowSub}>{label}</div>
      </div>
    </div>
  );
})()}


{/* age requirement (if configured) */}
{event?.ageGateOn ? (
  <div style={S.infoRowLast}>
    <div style={S.iconWrap}><LuBaby style={S.icon} /></div>
    <div>
      <div style={S.rowTitle}>Age Requirement</div>
      <div style={S.rowSub}>
        {ageLabelForDetails(event)}
      </div>
    </div>
  </div>
) : null}

      {/* Restricted Affinity line (only if event.affinity) */}
      {event?.affinity ? (
        <div style={S.infoRowLast}>
          <div style={S.iconWrap}><LuLock style={S.icon} /></div>
          <div>
            <div style={S.rowTitle}>Restricted Affinity</div>
            <div style={S.rowSub}>
              This event is limited to participants in a designated identity group. Please register only if this applies to you.
            </div>
          </div>
        </div>
      ) : null}

      {/* Ticket banner */}
      <div style={S.hairline} />
      <TicketCard
        eventTitle={name}
        eventDate={eventDate}
        startTime12={startTime12}
        endTime12={endTime12}
        hostedAt={hostedAt}
        ticket={userTicket}
        purchasedAt={userTicket?.purchasedAt}
        orderNumber={userTicket?.orderId || userTicket?._id}
        cta={userTicket ? 'View' : 'Get Ticket'}
        onView={() => {
          if (userTicket) {
            setOpenTicket(userTicket);
          } else if (onPriceClick) {
            onPriceClick();
          }
        }}
      />
      <div style={S.hairline} />

      {/* Sheet */}
      {openTicket && !showQr && (
        <TicketPurchasedSheet
          event={event}
          fullName={openTicket.fullName || ''}
          paymentMethod={openTicket.method || (price > 0 ? 'card' : 'free')}
          amount={openTicket.amount ?? price ?? 0}
          purchasedAt={openTicket.purchasedAt || fallbackPurchasedAt}
          orderId={openTicket.orderId}
          qrSrc={openTicket.qrUrl}
          onShowEvent={() => setOpenTicket(null)}
          onClose={() => setOpenTicket(null)}
          onOpenQr={() => {
            setQrForViewer(openTicket.qrUrl || null);
            setShowQr(true);
          }}
        />
      )}

      {/* QR viewer sheet */}
      {showQr && (
        <QrViewerSheet
          qrSrc={qrForViewer}
          onBack={() => {
            setShowQr(false);
            setOpenTicket(openTicket);
          }}
          onClose={() => {
            setShowQr(false);
            setOpenTicket(null);
          }}
        />
      )}

      <WhereToMeetSheet
  open={whereToMeetOpen}
  onClose={() => setWhereToMeetOpen(false)}
  title="Where to meet"
  spot={event?.whereToMeetSpot || "Front entrance by the patio."}
  note={
    event?.whereToMeetNote ||
    "Look for your host or check the group chat when you arrive."
  }
  imageUrl={event?.whereToMeetImageUrl}
  onDirections={() => {
    if (!event?.latitude || !event?.longitude) {
      alert("No location available for this gathering.");
      return;
    }

    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${event.latitude},${event.longitude}`,
      "_blank"
    );
  }}
/>
    </section>
  );
}

/* ───────────────────────── styles */
const S = {
  wrap: {
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    background: C.white,
    color: C.ink,
    padding: '8px 16px 0px',
  },
  dateLine: {
    textAlign: 'center', color: C.sub, fontWeight: 700, fontSize: 12, marginBottom: 6, textTransform: 'uppercase',
  },
  liveWrap: { display: 'flex', justifyContent: 'center', marginBottom: 6 },
  livePill: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '4px 10px', borderRadius: 999, background: '#DCFCE7' },
  liveText: { fontWeight: 800, fontSize: 12, color: '#059669', lineHeight: 1 },
  liveDotWrap: { position: 'relative', width: 10, height: 10 },
  liveDotCore: { position: 'absolute', inset: 0, borderRadius: '50%', background: '#059669' },
  liveDotPulse: { position: 'absolute', inset: 0, borderRadius: '50%', background: '#059669', opacity: 0.45, animation: 'livePulse 1.6s ease-out infinite' },
title: {
  maxWidth: 320,
  margin: '16px auto',
  color: '#111',
  fontFamily:
    "'Recoleta Alt', Georgia, 'Times New Roman', serif",
  fontSize: 'clamp(2.35rem, 7vw, 2.45rem)',
  lineHeight: 1.12,
  textAlign: 'center',
},

titleMain: {
  fontWeight: 700,
},

titleBracket: {
  fontWeight: 500,
  color: '#333',
},

  hostRowTop: { marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, flexWrap: 'wrap' },
  hostedAtLabel: { color: C.sub, fontSize: 12,textTransform: 'uppercase', fontWeight: 600 },
  venueChip: { borderRadius: 4, fontSize: 12, fontWeight: 800,textTransform: 'uppercase' },
  statsCard: {
    position: 'relative', background: '#fff', border: '0px solid ' + C.hair, borderRadius: 12,
    padding: '20px 8px', marginTop: 20, marginBottom: -10, marginLeft: 4, marginRight: 4,
  },
  statsRow: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', alignItems: 'center', gap: 35 },
  col: {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: 0,
  minWidth: 0,
  textAlign: 'center',
},

priceValue: {
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  fontWeight: 900,
  lineHeight: 1,
  letterSpacing: '-0.035em',
  whiteSpace: 'nowrap',
},

caption: {
  height: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: C.sub,
  fontSize: 10,
  fontWeight: 800,
  lineHeight: 1,
  whiteSpace: 'nowrap',
},

countBubble: {
  height: 24,
  minWidth: 24,
  borderRadius: 999,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#fff',
  color: C.ink,
  fontSize: 12,
  fontWeight: 900,
  lineHeight: 1,
},
  hairline: {
    height: 1,
    background: C.hair,
    margin: '16px 10px',
    marginRight: 10,
  },

 hostSection: {
  margin: '0 10px',
  padding: '0px 0 18px',
  borderBottom: '1px solid #EEF0F2',
},

  afterMeetHairline: {
    height: 1,
    background: '#EEF0F2',
    margin: '6px 10px 14px',
  },

  hostCard: {
    border: 'none',
    borderRadius: 0,
    background: 'transparent',
    padding: 0,
    margin: 0,
  },
  hostStrip: { display: 'grid', gridTemplateColumns: '40px 1fr auto', alignItems: 'center', gap: 4 },
  hostTextAndBadge: { display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 8 },
  idBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 999,
    color: '#E61010', background: '#FEECEC', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', height: 18,
  },
  idBadgeIcon: { fontSize: 16, color: '#E61010' },
  hostAvatar: {
    width: 30, height: 30, borderRadius: '50%', backgroundSize: 'cover', backgroundPosition: 'center',
    border: '2px solid #fff', boxShadow: '0 0 0 1px ' + C.hair,
  },


  reservedBubble: {
  minHeight: 36,
  padding: '0 9px',
  borderRadius: 999,
  background: '#B91C1C',
  color: '#fff',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 5,
  fontSize: 10.5,
  lineHeight: 1,
  fontWeight: 800,
  letterSpacing: '-0.015em',
  whiteSpace: 'nowrap',
},

reservedEmoji: {
  fontSize: 12,
  lineHeight: 1,
},

recommendedWrap: {
  display: 'flex',
  justifyContent: 'center',
  marginTop: 14,
  marginBottom: 10,
},

recommendedBadge: {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '8px 11px',
  borderRadius: 999,
 background: "#FFF6CC",
  color: "#000",
  border: '1px solid rgba(17,17,17,0.1)',
  fontSize: 11,
  lineHeight: 1,
  fontWeight: 800,
  letterSpacing: '-0.01em',
},

spotsValue: {
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#0B0B0B',
  fontSize: 12,
  lineHeight: 1,
  fontWeight: 900,
  letterSpacing: '-0.035em',
},

  participantsRow: {
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
},
  hostTitle: { fontWeight: 700, fontSize: 12 },
  hostSub: { color: C.sub, fontSize: 12, marginTop: 4, fontWeight: 600 },

  infoRow: {
    display: 'grid', gridTemplateColumns: '28px auto', alignItems: 'flex-start',
    gap: 5, marginLeft: 10, marginTop: 20,
  },
  infoRowLast: {
    display: 'grid', gridTemplateColumns: '28px auto', alignItems: 'flex-start',
    gap: 5, marginTop: 20, marginBottom: 6, marginRight: 20, marginLeft: 10,
  },
  iconWrap: { width: 28, marginLeft: 0, height: 28, display: 'grid', placeItems: 'center' },
  icon: { fontSize: 18, color: '#555' },
  rowTitle: { marginTop: 3, fontWeight: 700, fontSize: 12 },
  rowSub: { color: C.ink, opacity: 0.8, fontSize: 12, marginTop: 4, lineHeight: 1.35 },
  dot: { color: C.sub },
  buttonReset: { appearance: 'none', background: 'transparent', border: 'none', padding: 0, font: 'inherit', color: 'inherit' },
  clickBlock: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 },
  cardDivider: { position: 'absolute', top: '50%', transform: 'translate(-50%, -50%)', width: 1, height: 24, background: C.hair, borderRadius: 1, pointerEvents: 'none' },
};